import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { ProgressBarModule } from 'primeng/progressbar';
import { ToastModule } from 'primeng/toast';
import { tap } from 'rxjs';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import { PermissionService } from '../../../security/permission.service';
import { app_permissions } from '../../../security/app-permissions';
import { AccountsService } from '../../accounts.service';
import { Bill } from '../generated-bills/bill.model';
import { Voucher } from '../generated-vouchers/voucher.model';
import { accountingNumberWidth } from '../../shared/accounting-line-width';

interface VoucherAllocationRow {
    bill: Bill;
    selected: boolean;
    allocatedAmount: number;
    openAmount: number;
    discountAmount?: number;
    writeOffAmount?: number;
    bankChargeAmount?: number;
    withholdingAmount?: number;
    fxDifferenceAmount?: number;
    adjustmentsExpanded?: boolean;
}

@Component({
    selector: 'app-apply-voucher-bill',
    templateUrl: './apply-voucher-bill.component.html',
    styleUrls: ['./apply-voucher-bill.component.css', '../../shared/accounting-apply-layout.css'],
    providers: [MessageService],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        BreadcrumbModule,
        ButtonModule,
        ToastModule,
        ProgressBarModule,
            InputNumberModule,
        CheckboxModule,
        PageShellComponent,
        PageFormSectionComponent
    ]
})
export class ApplyVoucherBillComponent implements OnInit {
    readonly numberInputWidth = accountingNumberWidth;

    adjustmentsOpen(row: VoucherAllocationRow): boolean {
        if (row.selected && (this.getRowTotal(row) <= 0 || this.getRowTotal(row) > row.openAmount)) return true;
        return row.adjustmentsExpanded ?? !!(row.discountAmount || row.writeOffAmount || row.bankChargeAmount || row.withholdingAmount || row.fxDifferenceAmount);
    }

    toggleAdjustments(row: VoucherAllocationRow): void {
        row.adjustmentsExpanded = !this.adjustmentsOpen(row);
    }
    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = {};

    voucherId!: number;
    voucher!: Voucher;
    allocationRows: VoucherAllocationRow[] = [];
    totalAllocatedAmount = 0;
    balanceVoucherAmount = 0;
    voucherAmount = 0;
    isLoading = true;
    canApplyVoucher = false;

    constructor(
        private accountsService: AccountsService,
        private messageService: MessageService,
        private route: ActivatedRoute,
        private router: Router,
        private sessionStorageService: SessionStorageService,
        private permissionService: PermissionService
    ) {}

    ngOnInit(): void {
        this.canApplyVoucher = this.permissionService.hasPermission(app_permissions.accounts.voucher.edit);
        this.breadcrumbItems = [
            { label: 'Accounts', routerLink: '/accounts' },
            { label: 'Payables', routerLink: '/accounts/enterprise/payables/to-be-billed' },
            { label: 'Generated Vouchers', routerLink: '/accounts/enterprise/payables/generated-vouchers' },
            { label: 'Apply Voucher to Bill' }
        ];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };

        if (!this.canApplyVoucher) {
            this.isLoading = false;
            return;
        }

        this.route.params.subscribe(params => {
            this.voucherId = params['voucherId'];
            this.loadVoucherData();
        });
    }

    private loadVoucherData(): void {
        this.accountsService.getVoucherById(this.voucherId).subscribe(value => {
            this.voucher = value;
            this.voucherAmount = value.openAmount ?? value.amount ?? 0;
            this.balanceVoucherAmount = this.voucherAmount;
            this.loadUnpaidBills();
        });
    }

    loadUnpaidBills(): void {
		this.isLoading = true;
		const companyId = this.sessionStorageService.getCompany()?.id ?? 0;
		const party = this.voucher.mainParty;
		if (!party?.id?.entityId || !party.id.entityType) {
			this.allocationRows = [];
			this.isLoading = false;
			return;
		}
		this.accountsService.getGeneratedBills(0, 100, companyId, undefined, undefined, undefined, undefined, undefined, {
			postingStatuses: ['POSTED'],
			paymentStatuses: ['PARTIALLY_PAID', 'UNPAID'],
			partyId: party.id.entityId,
			partyType: party.id.entityType
		}).pipe(
            tap(res => {
                this.allocationRows = res.items.map(bill => ({
                    bill,
                    selected: false,
                    allocatedAmount: 0,
                    openAmount: bill.openAmount ?? bill.dueAmount ?? bill.totalAmount ?? 0,
                    discountAmount: 0,
                    writeOffAmount: 0,
                    bankChargeAmount: 0,
                    withholdingAmount: 0,
                    fxDifferenceAmount: 0
                }));
                this.calculateTotals();
            })
        ).subscribe({
            error: () => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Error loading unpaid bills'
                });
                this.isLoading = false;
            },
            complete: () => {
                this.isLoading = false;
            }
        });
    }

    onRowSelect(row: VoucherAllocationRow): void {
        if (row.selected) {
            const remaining = Math.max(0, this.voucherAmount - this.totalAllocatedAmount);
            row.allocatedAmount = Math.min(row.openAmount, remaining);
        } else {
            row.allocatedAmount = 0;
        }
        this.calculateTotals();
    }

    onAllocationChange(row: VoucherAllocationRow): void {
        row.allocatedAmount = this.nonNegative(row.allocatedAmount);
        row.discountAmount = this.nonNegative(row.discountAmount);
        row.writeOffAmount = this.nonNegative(row.writeOffAmount);
        row.bankChargeAmount = this.nonNegative(row.bankChargeAmount);
        row.withholdingAmount = this.nonNegative(row.withholdingAmount);
        row.fxDifferenceAmount = this.nonNegative(row.fxDifferenceAmount);
        row.selected = this.getRowTotal(row) > 0;
        if (row.discountAmount || row.writeOffAmount || row.bankChargeAmount || row.withholdingAmount || row.fxDifferenceAmount) row.adjustmentsExpanded = true;
        this.calculateTotals();
    }

    calculateTotals(): void {
        this.totalAllocatedAmount = this.allocationRows
            .filter(row => row.selected)
            .reduce((sum, row) => sum + this.getRowTotal(row), 0);
        this.balanceVoucherAmount = Math.max(0, this.voucherAmount - this.totalAllocatedAmount);
    }

    hasValidationError(): boolean {
        return this.totalAllocatedAmount > this.voucherAmount
            || this.allocationRows.some(row => row.selected && (this.getRowTotal(row) <= 0 || this.getRowTotal(row) > row.openAmount));
    }

    getRowTotal(row: VoucherAllocationRow): number {
        return (row.allocatedAmount || 0)
            + (row.discountAmount || 0)
            + (row.writeOffAmount || 0)
            + (row.bankChargeAmount || 0)
            + (row.withholdingAmount || 0)
            + (row.fxDifferenceAmount || 0);
    }

    getRowRemaining(row: VoucherAllocationRow): number {
        return Math.max(0, row.openAmount - this.getRowTotal(row));
    }

    private nonNegative(value?: number): number {
        return Math.max(0, Number(value || 0));
    }

    applyVoucher(): void {
        if (!this.canApplyVoucher) {
            this.messageService.add({ severity: 'warn', summary: 'Permission', detail: 'You do not have permission to apply vouchers.' });
            return;
        }

        const allocations = this.allocationRows
            .filter(row => row.selected && this.getRowTotal(row) > 0)
            .map(row => ({
                billId: row.bill.billId!,
                allocatedAmount: row.allocatedAmount,
                discountAmount: row.discountAmount,
                writeOffAmount: row.writeOffAmount,
                bankChargeAmount: row.bankChargeAmount,
                withholdingAmount: row.withholdingAmount,
                fxDifferenceAmount: row.fxDifferenceAmount
            }));

        if (allocations.length === 0) {
            this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Please select at least one bill.' });
            return;
        }

        if (this.hasValidationError()) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Validation',
                detail: 'Allocation amounts must be positive and cannot exceed bill or voucher open amounts.'
            });
            return;
        }

        this.isLoading = true;
        this.accountsService.applyVoucherAllocations(this.voucherId, { allocations }).subscribe({
            next: () => {
                this.isLoading = false;
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'The bills have been applied successfully.' });
                setTimeout(() => {
                    this.router.navigate(['/accounts/enterprise/payables/generated-vouchers']);
                }, 1000);
            },
            error: () => {
                this.isLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to apply voucher.' });
            }
        });
    }
}
